import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"

import {
  useGetOrgIntentionBoardsQuery,
  useDeleteOrgIntentionBoardMutation,
  useUpdateOrgIntentionBoardMutation,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

const EtDeleteOrgIntentionBoard = ({
  id,
  setActiveAdminView,
}) => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - To delete or deactivate a Trinity Intention Board.

  useTitle("Delete Intention Board")

  /*
  |--------------------------------------------------------------------------
  | 1 - Get Intention Board
  |--------------------------------------------------------------------------
  */

  const {
    orgIntentionBoard,
  } =
    useGetOrgIntentionBoardsQuery(
      undefined,
      {
        selectFromResult:
          ({
            data,
          }) => ({

            orgIntentionBoard:
              data
                ?.entities[id],

          }),
      }
    )

  /*
  |--------------------------------------------------------------------------
  | 2 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    !orgIntentionBoard
  ) {

    return (
      <PulseLoader
        color={"#ea580c"}
      />
    )

  }

  /*
  |--------------------------------------------------------------------------
  | 3 - Delete Form
  |--------------------------------------------------------------------------
  */

  return (

    <EtDeleteOrgIntentionBoardForm
      orgIntentionBoard={
        orgIntentionBoard
      }
      id={id}
      setActiveAdminView={
        setActiveAdminView
      }
    />

  )

}


/*
|--------------------------------------------------------------------------
| Delete Intention Board Form
|--------------------------------------------------------------------------
*/

const EtDeleteOrgIntentionBoardForm = ({
  orgIntentionBoard,
  id,
  setActiveAdminView,
}) => {

  const navigate =
    useNavigate()

  /*
  |--------------------------------------------------------------------------
  | 1 - State
  |--------------------------------------------------------------------------
  */

  const [
    isModalOpen,
    setIsModalOpen,
  ] =
    useState(
      false
    )

  const [
    deleteMode,
    setDeleteMode,
  ] =
    useState(
      "soft"
    )

  /*
  |--------------------------------------------------------------------------
  | 2 - Delete Mutation
  |--------------------------------------------------------------------------
  */

  const [
    deleteOrgIntentionBoard,
    {
      isLoading:
        isDeleting,

      isSuccess:
        isDeleteSuccess,

      isError:
        isDeleteError,

      error:
        deleteError,
    },
  ] =
    useDeleteOrgIntentionBoardMutation()

  /*
  |--------------------------------------------------------------------------
  | 3 - Update Mutation
  |--------------------------------------------------------------------------
  */

  const [
    updateOrgIntentionBoard,
    {
      isLoading:
        isUpdating,

      isSuccess:
        isUpdateSuccess,

      isError:
        isUpdateError,

      error:
        updateError,
    },
  ] =
    useUpdateOrgIntentionBoardMutation()

  /*
  |--------------------------------------------------------------------------
  | 4 - Form State
  |--------------------------------------------------------------------------
  */

  const [
    formData,
    setFormData,
  ] =
    useState({

      boardName:
        orgIntentionBoard
          ?.boardName ||
        "",

      publicSlug:
        orgIntentionBoard
          ?.publicSlug ||
        "",

      orgCode:
        orgIntentionBoard
          ?.orgCode ||
        "",

      headline:
        orgIntentionBoard
          ?.headline ||
        "",

      boardStatus:
        orgIntentionBoard
          ?.boardStatus ||
        "draft",

      isActive:
        orgIntentionBoard
          ?.isActive ??
        true,

      responseDeadline:
        orgIntentionBoard
          ?.responseDeadline ||
        "",

      timezone:
        orgIntentionBoard
          ?.timezone ||
        "America/Los_Angeles",

    })

  /*
  |--------------------------------------------------------------------------
  | 5 - Success
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      isDeleteSuccess ||
      isUpdateSuccess
    ) {

      setFormData({

        boardName:
          "",

        publicSlug:
          "",

        orgCode:
          "",

        headline:
          "",

        boardStatus:
          "draft",

        isActive:
          true,

        responseDeadline:
          "",

        timezone:
          "America/Los_Angeles",

      })

      if (
        setActiveAdminView
      ) {

        setActiveAdminView(
          "orgIntentionBoards"
        )

      } else {

        navigate(
          "/orgIntentionBoards"
        )

      }

    }

  }, [
    isDeleteSuccess,
    isUpdateSuccess,
  ])

  /*
  |--------------------------------------------------------------------------
  | 6 - Open Modal
  |--------------------------------------------------------------------------
  */

  const handleOpenModal =
    (
      mode =
        "soft"
    ) => {

      setDeleteMode(
        mode
      )

      setIsModalOpen(
        true
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 7 - Close Modal
  |--------------------------------------------------------------------------
  */

  const handleCloseModal =
    () =>
      setIsModalOpen(
        false
      )

  /*
  |--------------------------------------------------------------------------
  | 8 - Confirm
  |--------------------------------------------------------------------------
  */

  const handleConfirm =
    async e => {

      e.preventDefault()

      try {

        /*
        |--------------------------------------------------------------------------
        | Soft Delete
        |--------------------------------------------------------------------------
        */

        if (
          deleteMode ===
          "soft"
        ) {

          await updateOrgIntentionBoard({

            intentionBoardId:
              id,

            isActive:
              false,

          })
            .unwrap()

        } else {

          /*
          |--------------------------------------------------------------------------
          | Hard Delete
          |--------------------------------------------------------------------------
          */

          await deleteOrgIntentionBoard({

            intentionBoardId:
              id,

          })
            .unwrap()

        }

        if (
          setActiveAdminView
        ) {

          setActiveAdminView(
            "orgIntentionBoards"
          )

        } else {

          navigate(
            "/orgIntentionBoards"
          )

        }

      } catch (
        error
      ) {

        console.error(
          error
        )

      } finally {

        handleCloseModal()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 9 - Cancel
  |--------------------------------------------------------------------------
  */

  const handleCancel =
    () => {

      if (
        setActiveAdminView
      ) {

        setActiveAdminView(
          "orgIntentionBoards"
        )

      } else {

        navigate(
          "/orgIntentionBoards"
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 10 - Combined Loading / Error
  |--------------------------------------------------------------------------
  */

  const isLoading =
    isDeleting ||
    isUpdating

  const isError =
    isDeleteError ||
    isUpdateError

  const error =
    deleteError ||
    updateError

  /*
  |--------------------------------------------------------------------------
  | 11 - Format Deadline
  |--------------------------------------------------------------------------
  */

  const formatDeadline =
    dateValue => {

      if (
        !dateValue
      ) {

        return "Not Set"

      }

      return new Date(
        dateValue
      )
        .toLocaleString()

    }

  /*
  |--------------------------------------------------------------------------
  | 12 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">

      {/* Loading */}

      {isLoading && (

        <PulseLoader
          color={"#ea580c"}
        />

      )}

      {/* Error */}

      {isError && (

        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>

      )}

      <div className="bg-white p-1 m-1 rounded">

        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete Intention Board
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">

          <form
            onSubmit={
              e => {

                e.preventDefault()

                handleOpenModal(
                  "soft"
                )

              }
            }
            className="m-1 p-1 text-xs font-poppins"
          >

            {/* Board Name */}

            <input
              type="text"
              name="boardName"
              placeholder="Intention Board name"
              value={formData.boardName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            {/* Public Slug */}

            <input
              type="text"
              name="publicSlug"
              placeholder="Public slug"
              value={formData.publicSlug}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            {/* Organization Code */}

            <input
              type="text"
              name="orgCode"
              placeholder="Organization code"
              value={formData.orgCode}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            {/* Headline */}

            <textarea
              name="headline"
              placeholder="Intention Board headline"
              value={formData.headline}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
              rows={3}
            />

            {/* Board Status */}

            <input
              type="text"
              name="boardStatus"
              placeholder="Board status"
              value={formData.boardStatus}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            {/* Deadline */}

            <div className="w-full rounded p-2 m-1 bg-gray-100">

              <p className="text-[10px] font-bold text-gray-700">
                Response Deadline
              </p>

              <p className="text-xs text-gray-600">
                {
                  formatDeadline(
                    formData.responseDeadline
                  )
                }
              </p>

              <p className="text-[9px] text-gray-500">
                {formData.timezone}
              </p>

            </div>

            {/* Current Active State */}

            <div className="w-full rounded p-2 m-1 bg-gray-100">

              <p className="text-[10px] font-bold text-gray-700">
                Current Status
              </p>

              <p
                className={`text-xs font-bold ${
                  formData.isActive
                    ? "text-lime-700"
                    : "text-gray-500"
                }`}
              >
                {
                  formData.isActive
                    ? "Active"
                    : "Inactive"
                }
              </p>

            </div>

            {/* Guidance */}

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">

              <p className="text-xs text-orange-700">

                You can either deactivate this Intention Board or
                permanently delete it. Deactivation keeps the board
                available in Trinity history while preventing public use.

              </p>

              <p className="text-xs text-orange-700 mt-2">

                Hard delete permanently removes the Intention Board.
                The backend may prevent deletion where the board has
                already progressed into active candidate participation.

              </p>

            </div>

            {/* Actions */}

            <div
              id="orgIntentionBoards"
              className="bg-white m-1 px-1 py-3 rounded"
            >

              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={
                  () =>
                    handleOpenModal(
                      "soft"
                    )
                }
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={
                  () =>
                    handleOpenModal(
                      "hard"
                    )
                }
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={
                  handleCancel
                }
              />

            </div>

          </form>

        </div>

      </div>

      {/* Confirmation Modal */}

      <Modal
        isOpen={
          isModalOpen
        }
        onClose={
          handleCloseModal
        }
        onConfirm={
          handleConfirm
        }
      />

    </div>

  )

}

export default EtDeleteOrgIntentionBoard